import { useState } from "react";
import React from "react";
import {
  Users,
  UserCog,
  BarChart2,
  ChevronDown,
  ChevronRight,
  Section,
  Logs,
  MenuIcon,
  UserCheck,
  Wallet,
  Layers,
  User,
  History,
} from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { FourCircles } from "../../components/others/icons/Circles";

const Sidebar = ({ collapsed, toggleSidebar }) => {
  const [openSubMenus, setOpenSubMenus] = useState({});
  const navigate = useNavigate();
  const toggleSubMenu = (label) => {
    setOpenSubMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };
  const { auth } = useAuth();

  const menuItems = [
    {
      label: "Dashboard",
      icon: <FourCircles size={18} />,
      path: "/dashboard",
      permissions: ["9cf6c30f-4f90-7a12-19cc-52fcd5bb7c37"],
    },
    {
      label: "Loans",
      icon: <Wallet size={18} />,
      path: "/dashboard/loans",
      submenu: [
        {
          label: "Loan Applications",
          path: "/dashboard/loans-applications",
          permissions: ["d604def2-011d-3bab-5063-b98b4e95a37f"],
        },
        {
          label: "Loan Portfolio",
          path: "/dashboard/loans",
          permissions: ["d1020f15-2b28-ce95-f24d-ad4fe25cae0a"],
        },
        {
          label: "Loan Arrears",
          path: "/dashboard/loans-arrears",
          permissions: ["9cf6c30f-4f90-7a12-19cc-52fcd5bb7c37"],
        },
      ],
    },
    {
      label: "Transactions",
      icon: <History size={18} />,
      path: "/dashboard/transactions",
    },
    {
      label: "Borrowers",
      icon: <Users size={18} />,
      path: "/dashboard/borrowers",
      permissions: ["6e38d854-52ab-d1bd-1eb5-cddd02dbf15a"],
    },
    {
      label: "Guarantors",
      icon: <UserCheck size={18} />,
      path: "/dashboard/guarantors",
      permissions: ["24b33553-d740-5bde-5303-6c499f9e2fb7"],
    },
    {
      label: "Branches",
      icon: <Section size={18} />,
      path: "/dashboard/branches",
      permissions: ["dd6f5c4f-4ff4-ae95-e7b0-2c597e7e81c2"],
    },
    {
      label: "Users",
      icon: <UserCog size={18} />,
      path: "/dashboard/users",
      permissions: ["687584c5-946e-97d0-4791-d0dadda8f50b"],
    },
    {
      label: "Products",
      icon: <Layers size={18} />,
      path: "/dashboard/loan-products",
      permissions: ["414e701c-783c-8168-790e-f5c5bab6fe99"],
    },
    {
      label: "Reports & Analytics",
      icon: <BarChart2 size={18} />,
      path: "/dashboard/reports",
      permissions: ["9cf6c30f-4f90-7a12-19cc-52fcd5bb7c37"],
      submenu: [
        {
          label: "Reports",
          path: "/dashboard/reports",
          permissions: ["9cf6c30f-4f90-7a12-19cc-52fcd5bb7c37"],
        },
        {
          label: "Analytics",
          path: "/dashboard/analytics",
          permissions: ["9cf6c30f-4f90-7a12-19cc-52fcd5bb7c37"],
        },
      ],
    },
    {
      label: "Audit Trails",
      icon: <Logs size={18} />,
      path: "/dashboard/system-logs",
    },
  ];

  const hasPermission = (requiredPermissions = []) => {
    if (!requiredPermissions?.length) return true;

    const userPermissions = auth?.roles || [];
    return requiredPermissions.some((perm) =>
      userPermissions.some((userPerm) => userPerm.id === perm),
    );
  };

  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="w-[100%] h-full flex flex-col text-white relative">
        {!collapsed && (
          <div
            onClick={() => navigate("/dashboard/profile")}
            className="absolute cursor-pointer bg-[#222222] bottom-[20px] pl-[5%] w-full flex gap-[10px] items-center"
          >
            <div className="h-[42px] border w-[42px] rounded-full flex items-center justify-center">
              {auth?.user?.profile_image ? (
                <img
                  className="h-full w-full object-cover rounded-full"
                  src={auth?.user?.profile_image}
                  alt="profile"
                  onError={(e) => {
                    e.currentTarget.src = "/profile.png";
                  }}
                />
              ) : (
                <div className="h-[42px] flex items-center justify-center w-[42px] rounded-full overflow-hidden border-[0.7px] border-[#E4E4E4] cursor-pointer">
                  <User />
                </div>
              )}
            </div>
            <p className="text-[#F3F3F3] capitalize text-[12px] font-medium">
              {auth?.user?.firstname} {auth?.user?.lastname}
            </p>
          </div>
        )}
        {collapsed ? (
          <div className="bg-[#222222] h-[10%] flex items-center justify-center">
            <MenuIcon
              className="cursor-pointer"
              onClick={toggleSidebar}
              size={40}
              color="white"
            />
          </div>
        ) : (
          <div
            onClick={() => {
              if (
                hasPermission(
                  auth?.roles,
                  "9cf6c30f-4f90-7a12-19cc-52fcd5bb7c37",
                )
              ) {
                navigate("/dashboard");
              }
            }}
            className="flex h-[10%] p-[10px] bg-[#222222] items-center cursor-pointer gap-[5px]"
          >
            <div className="h-[40px] ml-[5px]">
              <img
                className="h-full w-full object-cover"
                src="/logo_noback.png"
                alt="logo"
              />
            </div>
            <p className="text-[#F3F3F3] ml-[10px] text-[24px] font-bold">
              Helacred
            </p>
          </div>
        )}
        <div className="py-4 w-[100%] h-[90%] overflow-y-auto p-[10px] bg-[#222222]">
          <ul className="space-y-2">
            {menuItems
              .filter((item) => hasPermission(item.permissions))
              .map((item) => (
                <li key={item.label} className="truncate">
                  {item.submenu ? (
                    <>
                      <button
                        onClick={() => toggleSubMenu(item.label)}
                        className={`flex w-full items-center justify-between px-3 py-2 rounded-md text-[16px] font-medium ${
                          openSubMenus[item.label]
                            ? "bg-[#FBF5F5] text-[#000000]"
                            : "text-[#ffffff] hover:bg-[#FBF5F5] hover:text-[#000000]"
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          {React.cloneElement(item.icon, {
                            size: collapsed ? 28 : 22,
                          })}
                          {!collapsed && <span>{item.label}</span>}
                        </span>
                        {!collapsed &&
                          (openSubMenus[item.label] ? (
                            <ChevronDown size={16} />
                          ) : (
                            <ChevronRight size={16} />
                          ))}
                      </button>

                      {openSubMenus[item.label] && (
                        <ul
                          className={`ml-8 mt-1 truncate space-y-1 ${
                            collapsed ? "hidden" : ""
                          }`}
                        >
                          {item.submenu
                            .filter((subItem) =>
                              hasPermission(subItem.permissions),
                            )
                            .map((subItem) => (
                              <li key={subItem.label}>
                                <NavLink
                                  to={subItem.path}
                                  className={({ isActive }) =>
                                    `block px-3 py-1.5 rounded-md text-[15px] ${
                                      isActive
                                        ? "bg-[#FBF5F5] text-[#000000]"
                                        : "text-[#ffffff] hover:bg-[#FBF5F5] hover:text-[#000000]"
                                    }`
                                  }
                                >
                                  {!collapsed && subItem.label}
                                </NavLink>
                              </li>
                            ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <NavLink
                      end
                      to={item.path}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 truncate rounded-md text-[16px] font-medium ${
                          isActive
                            ? "bg-[#FBF5F5] text-[#000000]"
                            : "text-[#ffffff] hover:bg-[#FBF5F5] hover:text-[#000000]"
                        }`
                      }
                    >
                      {React.cloneElement(item.icon, {
                        size: collapsed ? 28 : 22,
                      })}
                      {!collapsed && (
                        <span className="truncate">{item.label}</span>
                      )}
                    </NavLink>
                  )}
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
